import { FunctionComponent, ReactNode, useCallback, useMemo } from 'react'

import translate from '@i18n'
import useFmtCompetitionEntityIdsSorted from '@hooks/useFmtCompetitionEntityIdsSorted'
import useSportEntities from '@hooks/useSportEntities'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

import Filter from './Filter'
import TeamFilterSwiper from './TeamFilterSwiper'

type FmtTeamFilterProps = {
  selectedFilterId: string
  setSelectedFilterId: (value: string) => void
  currentSearchTerm: string
}

type TeamFilterTrackParameters = {
  filterAction: 'select' | 'switch' | 'reset'
  competitionId: string
  selectedFilterId: string
}

const FmtTeamFilter: FunctionComponent<FmtTeamFilterProps> = ({
  selectedFilterId,
  currentSearchTerm,
  setSelectedFilterId,
}) => {
  const { getSportEntityById } = useSportEntities()
  const competitionIds = useFmtCompetitionEntityIdsSorted()

  const onClickTracking = useCallback<
    TrackingFnType<TeamFilterTrackParameters>
  >(
    ({ extraData }) => ({
      event: `team_filter_${extraData.filterAction}`,
      element: extraData.competitionId,
      element_status:
        extraData.filterAction !== 'switch'
          ? getSportEntityById(extraData.competitionId)?.title
          : extraData.selectedFilterId,
    }),
    [getSportEntityById],
  )
  const trackOnClick = useTracking(onClickTracking)

  const handleFilterChange = useCallback(
    (competitionId: string) => {
      if (selectedFilterId === competitionId) {
        trackOnClick({
          filterAction: 'reset',
          competitionId,
          selectedFilterId,
        })
        setSelectedFilterId('')
      } else {
        setSelectedFilterId(competitionId)
        trackOnClick({
          filterAction: selectedFilterId ? 'switch' : 'select',
          competitionId,
          selectedFilterId,
        })
      }
    },
    [selectedFilterId, setSelectedFilterId, trackOnClick],
  )

  const filterItems = useMemo(
    () =>
      competitionIds.reduce(
        (acc, competitionId, competitionIdIndex, competitionIdsArray) => {
          const sportEntity = getSportEntityById(competitionId)

          if (sportEntity) {
            const { title: competitionTitle } = sportEntity

            acc.push(
              <Filter
                key={competitionId}
                competitionId={competitionId}
                competitionTitle={competitionTitle}
                onFilterChange={() => handleFilterChange(competitionId)}
                isSelected={selectedFilterId === competitionId}
              />,
            )
          }

          if (competitionIdIndex === competitionIdsArray.length - 1) {
            const otherCompetitionId = 'other'
            acc.push(
              <Filter
                key={otherCompetitionId}
                competitionId={otherCompetitionId}
                competitionTitle={translate('fmtFilter.otherCompetitions')}
                onFilterChange={() => handleFilterChange(otherCompetitionId)}
                isSelected={selectedFilterId === otherCompetitionId}
              />,
            )
          }

          return acc
        },
        [] as ReactNode[],
      ),
    [competitionIds, getSportEntityById, handleFilterChange, selectedFilterId],
  )

  return filterItems ? (
    <TeamFilterSwiper
      items={filterItems}
      currentSearchTerm={currentSearchTerm}
    />
  ) : null
}

export default FmtTeamFilter
